import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { MarketingShell } from "../components/MarketingShell";
import { getPublicSiteOrigin } from "../api/client";

export default function HowItWorksPage() {
  const origin = getPublicSiteOrigin() || "https://setpilot.org";
  const url = `${origin}/how-it-works`;
  const title = "How SetPilot works — DJ set order & harmonic mixing";
  const description =
    "Upload tracks, analyze BPM and Camelot key, pick a set profile, and get a suggested DJ order with transition ideas. Session-based, no login.";

  return (
    <MarketingShell>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>

      <article className="m-prose">
        <h1>How SetPilot works</h1>
        <p className="m-lede">
          SetPilot is a <strong>DJ set generator</strong> and <strong>harmonic mixing tool</strong>: it reads
          your files, estimates BPM, musical key (Camelot), and energy, then proposes an order and short transition notes.
          You stay in control — drag, reorder, and export when you want.
        </p>

        <h2>1. Upload your crate</h2>
        <p>
          Drop MP3 or WAV files in the browser. Each session uses a private ID; tracks are analyzed in the cloud and
          stored for that session so you can iterate on the set.
        </p>

        <h2>2. Analysis</h2>
        <p>
          The pipeline estimates <strong>BPM</strong>, <strong>key / Camelot code</strong>, and rough{" "}
          <strong>energy</strong> per track. That metadata powers harmonic and tempo-aware suggestions — a{" "}
          <strong>playlist optimizer</strong> for DJs, not a black-box autopilot.
        </p>

        <h2>3. Choose a profile</h2>
        <p>
          Pick a vibe — e.g. Smooth, Energetic, or Wild — so the suggested arc matches the story you want for the night.
        </p>

        <h2>4. Generate and tweak</h2>
        <p>
          Hit <strong>Generate set</strong> to get an ordered list and pairwise transition hints. Drag tracks to reorder;
          transitions update so you can audition the flow before the gig.
        </p>

        <h2>5. Share &amp; export (optional)</h2>
        <p>
          After optional email capture, you can download a PDF and create a <strong>shareable link</strong> for a read-only
          view of the set — useful for promo or collaboration.
        </p>

        <p style={{ marginTop: 28 }}>
          <Link to="/">← Back to the app</Link>
        </p>
      </article>
    </MarketingShell>
  );
}
